import React, {ReactNode, useCallback, useEffect, useRef} from 'react';

import {AuthProvider, useAuth} from './auth';
import {LocalizationProvider, useLocalization} from './localization';
import {CardsProvider} from './cards';
import {HelpersProvider} from './helpers';
import {GlobalProvider} from './global';
import {CarrinhosProvider} from './useCarrinhos';
import {apiv2} from '../services/api';
import {getPersistentDeviceId} from '../utils/deviceId';

const CITY_WAIT_MS = 8000;

function RastrearOnLogin() {
  const {isLogged} = useAuth();
  const {geolocation, isLocationLoading, locationIsActive} = useLocalization();
  const hasCalledRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const executeCall = useCallback((cidade?: string) => {
    if (hasCalledRef.current) return;
    hasCalledRef.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    (async () => {
      try {
        const device_id = await getPersistentDeviceId();
        await apiv2.post('/rastrear', {
          status_tela: 'home',
          ...(cidade && {cidade}),
          logado: true,
          device_id,
        });
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!isLogged) {
      hasCalledRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    if (hasCalledRef.current) return;
    if (isLocationLoading) return;

    if (locationIsActive && !geolocation?.cidade) {
      if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(() => executeCall(), CITY_WAIT_MS);
      }
      return;
    }

    executeCall(locationIsActive ? geolocation?.cidade : 'geoOff');
  }, [isLogged, isLocationLoading, geolocation?.cidade, locationIsActive, executeCall]);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  return null;
}

interface AppProviderProps {
  children: ReactNode;
}

function AppProvider({children}: AppProviderProps) {
  return (
    <CardsProvider>
      <CarrinhosProvider>
        <AuthProvider>
          <GlobalProvider>
            <HelpersProvider>
              <LocalizationProvider>
                <RastrearOnLogin />
                {children}
              </LocalizationProvider>
            </HelpersProvider>
          </GlobalProvider>
        </AuthProvider>
      </CarrinhosProvider>
    </CardsProvider>
  );
}

export {AppProvider};

